import { AccountReceivablesService, ScoreLogicService } from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import 'ag-grid-community/styles/ag-grid.css'; // Mandatory CSS required by the grid
import 'ag-grid-community/styles/ag-theme-quartz.min.css'; // Optional Theme applied to the grid
import { useState } from 'react';

export type ScoreLogicPageProps = {
  organizationId: string;
  scorelogicId: string;
};

interface MarkdownTextProps {
  text: string;
} 

export const ScoreLogicPage = ({
  organizationId,
  scorelogicId,
}: ScoreLogicPageProps) => {
  const [isOpen, setOpen] = useState(false);
  // get the current workspace

  const po = useStore($portfolio);
  // get the current organization

  const org = useStore($org);
  // get the current project
  const sl = useStore($scorelogic);
  // breadcrumb pages

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/score_logics`
        : '/organizations',
    },
    {
      label: sl?.name || 'Score Logic',
      href: `/organization/${org?.id}/score_logics/${scorelogicId}`,
    },
  ];

  const { theme } = useTheme();

  // get the account receivable
  // need to create controller for this
  const scoreLogicResult = useQuery({
    queryKey: ['scoreLogicResult', organizationId, scorelogicId],
    queryFn: async () => {
      const result = await ScoreLogicService.getScoreLogicById(
        organizationId,
        scorelogicId,
      );

      if (!result) {
        throw new Error('No data found');
      }
      return result;
    },
  });

  console.log(scoreLogicResult.data);

  const MarkdownText: React.FC<MarkdownTextProps> = ({ text }) => {
    // Function to convert markdown-like text to HTML
    const convertToHTML = (text: string): string => {
      // Replace '**Header**' with HTML <h1> or <h2> tags
      const formattedText = text
        .replace(/\*\*(.*?)\*\*/g, (_, header) => `<h2>${header}</h2>`) // Change <h2> to <h1> for main headers if needed
        .replace(/\n/g, '<br/>'); // Replace line breaks with <br> tags

      return formattedText;
    };

    // Convert text to HTML
    const htmlText = convertToHTML(text);

    return <div dangerouslySetInnerHTML={{ __html: htmlText }} />;
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>
      {/* Page Header */}
      <div className="mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {scoreLogicResult.data?.name || 'Score Logic details'}
          </h2>
          {scoreLogicResult.data?.createdAtUtc && (
            <p className="text-muted-foreground">
              Created on{' '}
              <time dateTime={scoreLogicResult.data?.createdAtUtc}>
                {new Date(scoreLogicResult.data?.createdAtUtc).toLocaleDateString()}
              </time>
            </p>
          )}
        </div>
      </div>
      {/* Project detail */}
      <div className="mt-4">
        <>
          <div className={cn('relative mt-4 rounded-md border p-4')}>
            <div className="flex items-center justify-between">
              <h2 className={cn('mb-2 text-lg font-semibold')}>
                Score Logic Details
              </h2>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  {/* Smart analyze and show/hide columns button used to exist here
                    Figure out what actions to add */}
                </div>

                <div className="relative">
                  {/* Download button used to exist here
                    Figure out what actions to add */}
                </div>
              </div>
            </div>
            <div className={cn('mt-4 grid grid-cols-2 gap-4')}>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Score Logic Name</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.name}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Event Type</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.eventType}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Description</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.description}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Condition</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.condition}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Impact Value</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.impactValue}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Priority</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.priority}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Active</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.isActive ? 'Yes' : 'No'}
                </p>
              </div>
              <div className="rounded-md border p-2">
                <p className={cn('text-sm font-medium')}>Account Receivables</p>
                <p className={cn('text-xl')}>
                  {scoreLogicResult.data?.accountReceivables?.map(
                    (ar) => ar.name,
                  )}
                </p>
              </div>
            </div>
          </div>
          <div></div>
        </>
      </div>
    </div>
  );
};
